import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import * as Yup from 'yup';
import { HostedFields, HostedFieldsTokenizePayload } from 'braintree-web';

import FmcForm from 'components/FmcForm';
import SplitPayPlaceOrderView from './SplitPayPlaceOrderView';
import { selectCreditApplication } from 'state/creditApplication';
import {
  SplitPayOffer,
  selectFinanceResult,
  selectFinanceOffersByType,
  getSplitPayOfferData,
} from 'state/financeOffers';
import useUnifiedDisclosures, {
  UnifiedDisclosuresResult,
} from 'hooks/requests/unifiedDisclosures';
import { selectTenant } from 'state/tenant';
import { useSnackbar } from 'notistack';
import useTranslate from 'hooks/useTranslate/useTranslate';
import useQueryParameters from 'hooks/useQueryParameters';

import useCheckout from '../useCheckout';
import usePrepareCheckout from '../usePrepareCheckout';
import useFeature from 'hooks/useFeature';

export interface FormValues {
  consent: boolean;
}

export type SplitPayPlaceOrderPropTypes = RouteComponentProps<{
  id: string;
}>;

type Payload = HostedFieldsTokenizePayload & {
  binData: {
    debit: string;
  };
};

const usePrerequisite = (props: SplitPayPlaceOrderPropTypes) => {
  const { bread } = useSelector(selectTenant);
  if (!bread) {
    // Should never happens
    throw new Error('Invalid tenant config');
  }

  const { intendedPurchaseAmount } = useSelector(selectCreditApplication);

  if (!intendedPurchaseAmount) {
    throw new Error('No intended purchase amount in state');
  }

  const financeResult = useSelector(selectFinanceResult);
  if (financeResult === null) {
    // Should not be possible to reach this page without the application.
    throw new Error('No application in state');
  }

  const { match } = props;
  const { id: financingOfferId } = match.params;

  const { splitPayOffers } = useSelector(selectFinanceOffersByType);
  const selectedOffer = useMemo(() => {
    return splitPayOffers.find(
      financeOffer => financeOffer.id === financingOfferId,
    );
  }, [financingOfferId, splitPayOffers]);

  if (!selectedOffer) {
    throw new Error(`Invalid finance offer id: ${financingOfferId}`);
  }

  return {
    bread,
    intendedPurchaseAmount,
    financeResult,
    selectedOffer,
  };
};

const SplitPayPlaceOrder: React.FC<SplitPayPlaceOrderPropTypes> = props => {
  const { shippingAddress, order } = useSelector(selectCreditApplication);

  const {
    intendedPurchaseAmount,
    financeResult: financeApplication,
    selectedOffer,
  } = usePrerequisite(props);

  const [unifiedDisclosureRequest] = useUnifiedDisclosures();
  const { enqueueSnackbar } = useSnackbar();
  const t = useTranslate();
  const { isEnabled } = useFeature();

  const schema = useMemo(
    () =>
      Yup.object().shape({
        consent: Yup.boolean()
          .test('isChecked', 'must consent', consent => consent)
          .default(false),
      }),
    [],
  );

  const initialValues: FormValues = {
    consent: false,
  } as FormValues;

  const [hasDisclosureError, setHasDisclosureError] = useState<boolean>(false);

  const handleDisclosureError = useCallback(() => {
    enqueueSnackbar(t('unified.placeOrder.error.genericSnackbarError'), {
      variant: 'error',
      persist: true,
    });
    setHasDisclosureError(true);
  }, [enqueueSnackbar, t]);

  const [disclosureLoading, setDisclosureLoading] = useState<{
    [type: string]: boolean;
  }>({ 'loan-agreement-sp': true });

  const setDisclosureLoadingState = useCallback(
    (type: string, loading: boolean) => {
      setDisclosureLoading({ [type]: loading });
    },
    [],
  );

  const getDisclosureLoadingState = useCallback(
    (type?: string) => {
      if (type) {
        return disclosureLoading[type];
      }
      for (const propName of Object.getOwnPropertyNames(disclosureLoading)) {
        if (disclosureLoading[propName]) {
          return true;
        }
      }
      return false;
    },
    [disclosureLoading],
  );

  const paymentAgreementId = selectedOffer.paymentAgreementId;

  const { mockBraintreeTokenize } = useQueryParameters();

  const [cachedDisclosureResult, setCachedDisclosureResult] = useState<
    UnifiedDisclosuresResult | undefined
  >(undefined);

  const getDisclosureResult = useCallback(async () => {
    try {
      setDisclosureLoadingState('loan-agreement-sp', true);
      if (!cachedDisclosureResult) {
        const cachedDisclosureResult = await unifiedDisclosureRequest({
          paymentAgreementId,
          type: 'loan-agreement-sp',
        });
        setCachedDisclosureResult(cachedDisclosureResult);
      }
      return cachedDisclosureResult;
    } catch (e) {
      handleDisclosureError();
    } finally {
      setDisclosureLoadingState('loan-agreement-sp', false);
    }
  }, [
    cachedDisclosureResult,
    handleDisclosureError,
    paymentAgreementId,
    setDisclosureLoadingState,
    unifiedDisclosureRequest,
  ]);

  useEffect(() => {
    getDisclosureResult();
  }, [getDisclosureResult]);

  const splitPayOfferData = useMemo(
    () =>
      getSplitPayOfferData(
        parseFloat(intendedPurchaseAmount),
        selectedOffer as SplitPayOffer,
      ),
    [intendedPurchaseAmount, selectedOffer],
  );

  // ======================================================================
  // Used by Brain Tree component and on submit callback function
  const [hostedFieldsInstance, setHostedFieldsInstance] = useState<
    HostedFields | undefined
  >(undefined);
  const [hostedFieldsValidity, setHostedFieldsValidity] = useState<boolean>(
    false,
  );
  // ======================================================================

  const checkoutFunction = isEnabled('financingWallet')
    ? usePrepareCheckout
    : useCheckout;

  const checkout = checkoutFunction(financeApplication, selectedOffer);

  const handleSubmit = useCallback(async () => {
    if (hostedFieldsInstance === undefined) {
      return;
    }

    if (!cachedDisclosureResult) {
      throw new Error(
        'loan-agreement must have been successfully fetced before submitting',
      );
    }
    let payload;
    try {
      if (mockBraintreeTokenize === 'success') {
        payload = (await Promise.resolve({
          nonce: 'samplenonce',
          binData: {
            debit: 'Yes',
          },
        })) as Payload;
      } else if (mockBraintreeTokenize === 'error') {
        const braintreeError = new Error();
        braintreeError.name = 'BraintreeError';
        payload = await Promise.reject(braintreeError);
      } else {
        payload = (await hostedFieldsInstance.tokenize({
          vault: true,
        })) as Payload;
      }
    } catch (e) {
      if (e.name === 'BraintreeError') {
        enqueueSnackbar(
          t('unified.placeOrder.hostedFieldTokenizeErrorMessage'),
          {
            variant: 'error',
            persist: true,
          },
        );
        return;
      } else {
        throw e;
      }
    }

    const nonce = payload.nonce;
    const debit = payload.binData.debit === 'Yes';

    await checkout({
      disclosureChecksum: cachedDisclosureResult.disclosureChecksum,
      braintreeNonce: nonce,
      braintreeCardType: debit ? 'DEBIT' : 'CREDIT',
    });
  }, [
    hostedFieldsInstance,
    cachedDisclosureResult,
    checkout,
    mockBraintreeTokenize,
    enqueueSnackbar,
    t,
  ]);

  return (
    <FmcForm
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={schema}
    >
      {formProps => (
        <SplitPayPlaceOrderView
          order={order}
          shippingAddress={shippingAddress}
          splitPayOfferData={splitPayOfferData}
          hostedFieldsValidity={hostedFieldsValidity}
          hostedFieldsInstance={hostedFieldsInstance}
          biweeklyPayment={splitPayOfferData.biweeklyPayment}
          setHostedFieldsInstance={setHostedFieldsInstance}
          setHostedFieldsValidity={setHostedFieldsValidity}
          paymentAgreementId={paymentAgreementId}
          cachedLoanAgreementDisclosureContent={
            cachedDisclosureResult
              ? cachedDisclosureResult.disclosureContent
              : ''
          }
          hasDisclosureError={hasDisclosureError}
          handleDisclosureError={handleDisclosureError}
          getDisclosureLoadingState={getDisclosureLoadingState}
          setDisclosureLoadingState={setDisclosureLoadingState}
          {...formProps}
        />
      )}
    </FmcForm>
  );
};

export default SplitPayPlaceOrder;
